import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useProfile } from '../../hooks';
import { profileApi } from '../../services/backendApi';
import { useTenants } from '../../contexts/TenantContext';
import { Card, CardHeader, CardBody, Button, Avatar, Spinner, Alert } from '../../components/ui';
import { getFieldOfWorkLabel } from '../../utils';
import type { ManagerDashboardStats, Profile } from '../../types';

const StatCard: React.FC<{
  value: number;
  label: string;
  color: string;
  bg: string;
  icon: string;
  to?: string;
}> = ({ value, label, color, bg, icon, to }) => {
  const inner = (
    <CardBody>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
          <svg className={`w-5 h-5 ${color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
          </svg>
        </div>
        <div>
          <p className={`text-2xl font-extrabold leading-none ${color}`}>{value}</p>
          <p className="text-xs text-gray-500 mt-0.5 font-medium">{label}</p>
        </div>
      </div>
    </CardBody>
  );

  if (!to) return <Card>{inner}</Card>;
  return (
    <Link to={to} className="block transition-opacity hover:opacity-90">
      <Card>{inner}</Card>
    </Link>
  );
};

const EMPTY_STATS: ManagerDashboardStats = {
  employee_count: 0,
  document_count: 0,
  verified_document_count: 0,
  unverified_document_count: 0,
  awaiting_verification_employee_count: 0,
  complete_employee_count: 0,
};

export const ManagerDashboard: React.FC = () => {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const { currentTenant } = useTenants();
  const [recentEmployees, setRecentEmployees] = useState<Profile[]>([]);
  const [stats, setStats] = useState<ManagerDashboardStats>(EMPTY_STATS);
  const [statsLoading, setStatsLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStatsLoading(true);
    profileApi
      .getDashboardStats()
      .then(result => setStats(result ?? EMPTY_STATS))
      .catch(() => setError('Failed to load dashboard stats'))
      .finally(() => setStatsLoading(false));

    profileApi
      .getWorkers({ limit: 5 })
      .then(result => setRecentEmployees(result ?? []))
      .catch(() => {})
      .finally(() => setEmployeesLoading(false));
  }, []);

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const displayName = profile?.first_name || user?.firstName || user?.email?.split('@')[0] || 'there';
  const tenantName = currentTenant?.name ?? 'Your organisation';
  const needsAttention =
    stats.unverified_document_count > 0 || stats.awaiting_verification_employee_count > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          Welcome back, {displayName} · {tenantName}
        </p>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>
      )}

      {needsAttention && !statsLoading && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 bg-sky-50 border border-sky-200 rounded-xl">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-sky-900">Verification attention needed</p>
              <p className="text-xs text-sky-700 mt-0.5">
                {stats.unverified_document_count} unverified document{stats.unverified_document_count !== 1 ? 's' : ''}
                {stats.awaiting_verification_employee_count > 0
                  ? ` across ${stats.awaiting_verification_employee_count} employee${stats.awaiting_verification_employee_count !== 1 ? 's' : ''}`
                  : ''}
                .
              </p>
            </div>
          </div>
          <Link to="/manager/workers">
            <Button size="sm" variant="secondary">Review employees</Button>
          </Link>
        </div>
      )}

      {/* Insight stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardBody>
                <div className="h-12 bg-gray-100 rounded-lg animate-pulse" />
              </CardBody>
            </Card>
          ))
        ) : (
          <>
            <StatCard
              value={stats.employee_count}
              label="Employees"
              color="text-brand-600"
              bg="bg-brand-50"
              icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              to="/manager/workers"
            />
            <StatCard
              value={stats.document_count}
              label="Documents"
              color="text-amber-600"
              bg="bg-amber-50"
              icon="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              to="/manager/workers"
            />
            <StatCard
              value={stats.verified_document_count}
              label="Verified docs"
              color="text-emerald-600"
              bg="bg-emerald-50"
              icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              to="/manager/workers"
            />
            <StatCard
              value={stats.unverified_document_count}
              label="Needs verification"
              color="text-sky-600"
              bg="bg-sky-50"
              icon="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              to="/manager/workers"
            />
          </>
        )}
      </div>

      {/* Secondary snapshot */}
      {!statsLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card>
            <CardBody>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Passport complete</p>
              <p className="text-xl font-extrabold text-gray-900 mt-1">
                {stats.complete_employee_count}
                <span className="text-sm font-medium text-gray-400 ml-1.5">
                  of {stats.employee_count} employees
                </span>
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Awaiting verification</p>
              <p className="text-xl font-extrabold text-sky-700 mt-1">
                {stats.awaiting_verification_employee_count}
                <span className="text-sm font-medium text-gray-400 ml-1.5">employees</span>
              </p>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Recent employees */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3 min-w-0">
            <h2 className="text-lg font-semibold leading-snug">Recent Employees</h2>
            <Link to="/manager/workers" className="ml-auto text-xs font-semibold text-brand-600 hover:text-brand-700 whitespace-nowrap shrink-0 pt-1">
              View all
            </Link>
          </div>
        </CardHeader>
        <CardBody>
          {employeesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : recentEmployees.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {recentEmployees.map(emp => {
                const empName = [emp.first_name, emp.last_name].filter(Boolean).join(' ') || 'Anonymous Employee';
                return (
                  <Link
                    key={emp.user_id}
                    to={`/manager/workers/${emp.id}`}
                    className="flex items-center gap-3 py-3 hover:bg-gray-50 -mx-6 px-6 transition-colors first:-mt-2 last:-mb-2"
                  >
                    <Avatar
                      src={emp.photo_url}
                      alt={empName}
                      size="sm"
                      fallback={emp.first_name?.charAt(0) || '?'}
                      className="flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{empName}</p>
                      {emp.field_of_work && (
                        <p className="text-xs text-gray-500 truncate">{getFieldOfWorkLabel(emp.field_of_work)}</p>
                      )}
                    </div>
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-6">No employees registered yet</p>
          )}
        </CardBody>
      </Card>

      {/* My profile card */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">My Profile</h2>
        </CardHeader>
        <CardBody>
          <div className="flex items-center gap-3 mb-4">
            <Avatar
              src={profile?.photo_url}
              alt={displayName}
              size="md"
              fallback={displayName.charAt(0).toUpperCase()}
            />
            <div>
              <p className="font-medium">
                {[profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || user?.email}
              </p>
              {profile?.company_name && (
                <p className="text-sm text-gray-500">{profile.company_name}</p>
              )}
            </div>
          </div>
          <Link to="/manager/profile">
            <Button variant="secondary" size="sm">Edit Profile</Button>
          </Link>
        </CardBody>
      </Card>
    </div>
  );
};
